/**
 * PricingPage — 3 tier cards + feature comparison table.
 * Responsive: mobile/tablet/desktop (CSS grid + min-width breakpoints).
 * Copy uses approved language only (see /app/memory/PRD.md banned-terms list).
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { SiteNav } from './SiteNav';
import { Footer } from './Footer';
import { useAuth } from '../contexts/AuthContext';
import { Check, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Approved copy — never alter without a spec update
const TIERS = [
  {
    key: 'free',
    name: 'Free',
    price: '£0',
    cadence: 'forever',
    description: 'Try the drawing tool, save two projects, buy documents one-off when you need them.',
    features: [
      'Access the drawing canvas and pricing tool',
      '2 saved projects',
      'Buy any document one-off — pay per-document',
      'No monthly or annual fee',
    ],
    cta: 'Current plan',
  },
  {
    key: 'pro',
    name: 'Pro',
    price: '£199',
    cadence: 'per year',
    description: 'For investors and homeowners managing multiple refurbishment projects.',
    features: [
      '5 saved projects',
      '2 Full Project Pack downloads per month',
      'Built from real project data',
      'Priority email support',
    ],
    cta: 'Upgrade to Pro',
  },
  {
    key: 'pro_plus',
    name: 'Pro+',
    price: '£599',
    cadence: 'per year',
    description: 'Everything in Pro, plus regular consultation calls with a chartered Quantity Surveyor.',
    features: [
      'Everything in Pro',
      '15 saved projects',
      '4 Full Project Pack downloads per month',
      '4 Project Cost Reviews per year',
      'Priority Project Cost Review scheduling',
    ],
    cta: 'Upgrade to Pro+',
    highlight: true,
  },
];

// Feature comparison table — rows locked per spec
const COMPARE_ROWS = [
  { label: 'Drawing tool and pricing estimator', free: true, pro: true, pro_plus: true },
  { label: 'Saved projects', free: '2', pro: '5', pro_plus: '15' },
  { label: 'Pay-per-document checkout (Full Project Pack £199)', free: true, pro: true, pro_plus: true },
  { label: 'Full Project Pack monthly allowance', free: '—', pro: '2 per month', pro_plus: '4 per month' },
  { label: 'Project Cost Reviews', free: '—', pro: '—', pro_plus: '4 per year' },
  { label: 'Built from real project data', free: true, pro: true, pro_plus: true },
  { label: 'Structured using QS principles', free: true, pro: true, pro_plus: true },
  { label: 'Adjusted for current market conditions and location', free: true, pro: true, pro_plus: true },
  { label: 'Priority email support', free: false, pro: true, pro_plus: true },
  { label: 'Priority Project Cost Review scheduling', free: false, pro: false, pro_plus: true },
];

const FeatureCell = ({ value }) => {
  if (value === true) return <Check size={18} style={{ color: '#10B981' }} data-testid="feature-check" />;
  if (value === false) return <X size={16} style={{ color: '#94A3B8' }} data-testid="feature-x" />;
  return <span style={{ fontSize: 13, color: '#334155' }}>{value}</span>;
};

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [loadingTier, setLoadingTier] = useState(null);
  const [error, setError] = useState('');
  const wasCancelled = params.get('cancelled') === 'true';
  const currentTier = user?.tier || 'free';

  const handleSubscribe = async (tierKey) => {
    if (user === false) {
      navigate(`/login?next=/pricing?subscribe=${tierKey}`);
      return;
    }
    setError('');
    setLoadingTier(tierKey);
    try {
      const res = await axios.post(`${API}/billing/subscribe`, { tier: tierKey });
      if (res.data?.url) window.location.href = res.data.url;
      else throw new Error('No checkout URL returned');
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Checkout failed');
      setLoadingTier(null);
    }
  };

  return (
    <div style={{ background: '#F7F8FA', minHeight: '100vh' }}>
      <SiteNav />

      {/* Scoped responsive CSS — keeps it self-contained without a new .css file */}
      <style>{`
        .pricing-hero { padding: 56px 24px 28px; max-width: 1100px; margin: 0 auto; text-align: center; }
        .pricing-hero h1 { font-family: 'DM Serif Display', serif; font-size: 44px; margin: 0; color: #0F2742; }
        .pricing-hero p { color: #64748B; font-size: 16px; max-width: 560px; margin: 12px auto 0; line-height: 1.55; }
        .pricing-grid { display: grid; grid-template-columns: 1fr; gap: 18px; max-width: 1100px; margin: 0 auto; padding: 8px 24px 24px; }
        @media (min-width: 720px) { .pricing-grid { grid-template-columns: repeat(3, 1fr); gap: 20px; } }
        .pricing-card { background: #fff; border-radius: 16px; padding: 28px 24px 24px; position: relative; display: flex; flex-direction: column; border: 1.5px solid #E2E8F0; }
        .pricing-card.highlight { border: 2.5px solid #2563EB; box-shadow: 0 8px 30px rgba(37,99,235,.12); }
        .pricing-card .tier-name { font-size: 13px; font-weight: 700; color: #64748B; text-transform: uppercase; letter-spacing: .08em; }
        .pricing-card .tier-price { font-family: 'DM Serif Display', serif; font-size: 38px; color: #0F2742; }
        .pricing-card .tier-cadence { color: #64748B; font-size: 14px; margin-left: 6px; }
        .pricing-card .tier-desc { color: #475569; font-size: 13px; margin: 8px 0 18px; min-height: 48px; line-height: 1.5; }
        .pricing-card ul { list-style: none; padding: 0; margin: 0; flex: 1; }
        .pricing-card li { display: flex; align-items: flex-start; gap: 10px; font-size: 13px; color: #0F2742; margin-bottom: 10px; line-height: 1.45; }
        .pricing-card button.subscribe { margin-top: 22px; padding: 12px 16px; border-radius: 10px; border: none; font-size: 14px; font-weight: 700; cursor: pointer; width: 100%; }
        .pricing-card .most-popular { position: absolute; top: -12px; left: 20px; background: #2563EB; color: #fff; font-size: 10px; font-weight: 800; letter-spacing: .1em; padding: 4px 10px; border-radius: 999px; }

        /* Comparison table — responsive */
        .compare-wrap { max-width: 1100px; margin: 48px auto 0; padding: 0 24px 56px; }
        .compare-wrap h2 { font-family: 'DM Serif Display', serif; font-size: 28px; color: #0F2742; margin: 0 0 16px; }
        .compare-table { width: 100%; border-collapse: collapse; background: #fff; border-radius: 14px; overflow: hidden; box-shadow: 0 2px 20px rgba(15,39,66,.05); font-size: 13px; }
        .compare-table thead th { background: #0F2742; color: #fff; padding: 14px 12px; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; }
        .compare-table thead th:first-child { text-align: left; }
        .compare-table tbody td { padding: 13px 12px; border-top: 1px solid #F1F5F9; text-align: center; }
        .compare-table tbody td:first-child { text-align: left; color: #0F2742; font-weight: 500; }
        .compare-table tbody tr:hover { background: #F8FAFC; }
        .compare-table th.highlighted { background: #2563EB; }
        @media (max-width: 640px) {
          .compare-wrap h2 { font-size: 22px; }
          .compare-table { font-size: 11.5px; }
          .compare-table thead th, .compare-table tbody td { padding: 10px 8px; }
        }
        .pricing-footer { max-width: 1100px; margin: 0 auto; padding: 0 24px 56px; text-align: center; font-size: 13px; color: #64748B; line-height: 1.7; }
        .pricing-footer a { color: #2563EB; }
      `}</style>

      <div className="pricing-hero">
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.14em', color: '#0F2742', textTransform: 'uppercase', marginBottom: 8 }}>
          Pricing
        </div>
        <h1>Plans for every project cadence</h1>
        <p>Start free. Upgrade when you need multiple document downloads a month or Project Cost Reviews with a chartered Quantity Surveyor. Cancel anytime — access continues until the end of the billing period.</p>
      </div>

      {wasCancelled && (
        <div data-testid="pricing-cancelled-banner" style={{ maxWidth: 1100, margin: '0 auto 20px', padding: '0 24px' }}>
          <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', color: '#92400E', padding: '12px 16px', borderRadius: 10, fontSize: 14 }}>
            Checkout cancelled — your account is unchanged. Ready whenever you are.
          </div>
        </div>
      )}
      {error && (
        <div data-testid="pricing-error" style={{ maxWidth: 1100, margin: '0 auto 20px', padding: '0 24px' }}>
          <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B', padding: '12px 16px', borderRadius: 10, fontSize: 14 }}>
            {error}
          </div>
        </div>
      )}

      <div className="pricing-grid">
        {TIERS.map((t) => {
          const isCurrent = currentTier === t.key;
          const ctaLabel = isCurrent ? 'Current plan'
            : (loadingTier === t.key ? 'Opening Stripe…' : t.cta);
          const btnDisabled = isCurrent || t.key === 'free' || loadingTier === t.key;
          return (
            <div
              key={t.key}
              data-testid={`pricing-card-${t.key}`}
              className={`pricing-card ${t.highlight ? 'highlight' : ''}`}
            >
              {t.highlight && <div className="most-popular">MOST POPULAR</div>}
              <div className="tier-name">{t.name}</div>
              <div style={{ marginTop: 10, marginBottom: 4 }}>
                <span className="tier-price">{t.price}</span>
                <span className="tier-cadence">{t.cadence}</span>
              </div>
              <p className="tier-desc">{t.description}</p>
              <ul>
                {t.features.map((f) => (
                  <li key={f}>
                    <Check size={16} style={{ color: '#10B981', flexShrink: 0, marginTop: 2 }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid={`subscribe-btn-${t.key}`}
                onClick={() => !btnDisabled && handleSubscribe(t.key)}
                disabled={btnDisabled}
                className="subscribe"
                style={{
                  background: isCurrent || t.key === 'free' ? '#E2E8F0' : (t.highlight ? '#2563EB' : '#0F2742'),
                  color: isCurrent || t.key === 'free' ? '#64748B' : '#fff',
                  cursor: btnDisabled ? 'not-allowed' : 'pointer',
                }}
              >
                {ctaLabel}
              </button>
            </div>
          );
        })}
      </div>

      {/* Feature comparison table */}
      <section className="compare-wrap" data-testid="feature-comparison">
        <h2>Full feature comparison</h2>
        <div style={{ overflowX: 'auto' }}>
          <table className="compare-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Free</th>
                <th>Pro</th>
                <th className="highlighted">Pro+</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map((row) => (
                <tr key={row.label} data-testid={`compare-row-${row.label.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
                  <td>{row.label}</td>
                  <td><FeatureCell value={row.free} /></td>
                  <td><FeatureCell value={row.pro} /></td>
                  <td><FeatureCell value={row.pro_plus} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="pricing-footer">
        All plans billed in GBP. Cancel anytime — changes take effect at the end of your current billing period.
        <br />
        Allowances reset on your billing anniversary date and do not roll over. Project Cost Reviews reset once per year on your annual renewal.
        <br />
        <a href="/terms">Read the full Terms and Conditions</a>
        {' · '}
        <a href="/contact">Contact us</a>
      </div>

      <Footer />
    </div>
  );
}
